// src/pages/profile/sections/UserProfileTimezone.tsx
import React, { useMemo } from "react";
import { Select, Col, Typography } from "antd";
import { User } from "../../../types/admin/UserTypes";
import { deviceZone } from "../../../utils/time";

const { Text } = Typography;

// `Intl.supportedValuesOf` is absent from TypeScript 4.9's lib, so it is narrowed rather than cast.
type IntlWithSupportedValues = typeof Intl & {
	supportedValuesOf?: (key: "timeZone") => string[];
};

// Australian zones and UTC first — this is an Australian platform; `showSearch` reaches the rest.
const isPreferredZone = (timezone: string): boolean => timezone.startsWith("Australia/") || timezone === "UTC";

// Deliberately duplicated from AppointmentSettingsTab: extracting it would mean editing that file.
const IANA_TIMEZONES: string[] = (() => {
	// This IIFE runs at MODULE scope — an uncaught throw here white-screens the whole profile page.
	try {
		const intl = Intl as IntlWithSupportedValues;
		const zones = typeof intl.supportedValuesOf === "function" ? intl.supportedValuesOf("timeZone") : [];

		return [...zones.filter(isPreferredZone), ...zones.filter((zone) => !isPreferredZone(zone))];
	} catch {
		return [];
	}
})();

// A Select cannot carry null as a distinguishable option value, so "no zone saved" gets a sentinel.
const DEVICE_ZONE = "__device__";

interface UserProfileTimezoneProps {
	user: User;
	isEditing: boolean;
	setUserData: React.Dispatch<React.SetStateAction<User>>;
}

const UserProfileTimezone: React.FC<UserProfileTimezoneProps> = ({ user, isEditing, setUserData }) => {
	const saved = user.timezone || null;
	const deviceLabel = `Use my device timezone (${deviceZone()})`;

	// The saved zone stays selectable even if this browser's Intl does not list it — otherwise
	// opening the form would silently blank a zone the user had already set.
	const options = useMemo(() => {
		const zones = saved && !IANA_TIMEZONES.includes(saved) ? [saved, ...IANA_TIMEZONES] : IANA_TIMEZONES;

		return [{ label: deviceLabel, value: DEVICE_ZONE }, ...zones.map((zone) => ({ label: zone, value: zone }))];
	}, [saved, deviceLabel]);

	// Explicitly null, never undefined: JSON.stringify drops undefined keys, so the clear would
	// never reach the API and "go back to my device zone" would silently do nothing.
	const handleChange = (value: string) => {
		setUserData((prevUser) => ({ ...prevUser, timezone: value === DEVICE_ZONE ? null : value }));
	};

	return (
		<Col xs={24} sm={12} md={8}>
			<Text type="secondary" style={{ fontSize: 11 }}>Timezone</Text>
			{isEditing ? (
				<Select
					showSearch
					id="profile-timezone"
					value={saved ?? DEVICE_ZONE}
					onChange={handleChange}
					style={{ width: "100%", marginTop: 4 }}
					filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
					options={options}
				/>
			) : (
				<Text style={{ fontSize: 13, display: "block", marginTop: 4 }}>{saved ?? deviceLabel}</Text>
			)}
			<Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
				Dates and times are shown to you in this timezone.
			</Text>
		</Col>
	);
};

export default UserProfileTimezone;
